import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mountain,
  TrendingUp,
  Users,
  Building2,
  Globe2,
  Cloud,
  CloudRain,
  CloudSnow,
  Sun,
  Wind,
  CheckCircle2,
  Circle,
  Flag,
  MapPin,
  Info,
  Sparkles,
  ArrowUp,
  Eye
} from 'lucide-react';

// Mountain faces representing role scopes
type RoleFace = 'individual' | 'team' | 'organization' | 'ecosystem';

// Weather conditions representing STARS contexts
type WeatherCondition = 'stable' | 'turbulent' | 'accelerating' | 'realigning' | 'startup';

// Elevation camps representing progression stages
interface ElevationCamp {
  elevation: number;
  name: string;
  description: string;
  degapStage: string;
  capabilities: string[];
  color: string;
}

// Climbing routes representing the 12 capability dimensions
interface ClimbingRoute {
  id: string;
  name: string;
  face: RoleFace;
  difficulty: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  description: string;
  relatedFramework?: string;
}

const elevationCamps: ElevationCamp[] = [
  {
    elevation: 1000,
    name: 'Base Camp: Decide',
    description: 'The starting point - commitment to the journey and initial framing',
    degapStage: 'Decide',
    capabilities: ['Awareness', 'Recognition', 'Commitment'],
    color: 'amber'
  },
  {
    elevation: 2500,
    name: 'Lower Camp: Engage',
    description: 'Building alignment and enrolling stakeholders in the ascent',
    degapStage: 'Engage',
    capabilities: ['Stakeholder Alignment', 'Shared Understanding', 'Motivation'],
    color: 'coral'
  },
  {
    elevation: 4000,
    name: 'Middle Camp: Gather',
    description: 'Collecting data, insights, and perspectives systematically',
    degapStage: 'Gather',
    capabilities: ['Data Collection', 'Pattern Recognition', 'Information Synthesis'],
    color: 'primary'
  },
  {
    elevation: 5500,
    name: 'High Camp: Arrange',
    description: 'Organizing insights into coherent structures and frameworks',
    degapStage: 'Arrange',
    capabilities: ['Structuring', 'Categorization', 'Visualization'],
    color: 'sage'
  },
  {
    elevation: 7000,
    name: 'Summit: Prioritize',
    description: 'Making strategic choices and sequencing action with clarity',
    degapStage: 'Prioritize',
    capabilities: ['Evaluation', 'Tradeoff Intelligence', 'Decision Execution'],
    color: 'plum'
  }
];

const climbingRoutes: ClimbingRoute[] = [
  // Individual Contributor Face
  {
    id: 'clarity-of-focus',
    name: 'Clarity of Focus',
    face: 'individual',
    difficulty: 'beginner',
    description: 'The ability to identify and maintain focus on what matters most',
    relatedFramework: 'pf.framework.diagnostic.capabilities.12'
  },
  {
    id: 'conceptual-precision',
    name: 'Conceptual Precision',
    face: 'individual',
    difficulty: 'intermediate',
    description: 'Using the right mental models and frameworks with precision',
    relatedFramework: 'pf.framework.diagnostic.capabilities.12'
  },
  {
    id: 'tool-fluency',
    name: 'Tool Fluency',
    face: 'individual',
    difficulty: 'beginner',
    description: 'Skillful use of prioritization tools and techniques',
    relatedFramework: 'pf.framework.diagnostic.capabilities.12'
  },
  
  // Team Face
  {
    id: 'stakeholder-intelligence',
    name: 'Stakeholder Intelligence',
    face: 'team',
    difficulty: 'intermediate',
    description: 'Understanding and navigating stakeholder needs and dynamics',
    relatedFramework: 'pf.framework.diagnostic.capabilities.12'
  },
  {
    id: 'cultural-reinforcement',
    name: 'Cultural Reinforcement',
    face: 'team',
    difficulty: 'advanced',
    description: 'Building teams and cultures that naturally prioritize well',
    relatedFramework: 'pf.framework.diagnostic.capabilities.12'
  },
  {
    id: 'rhythm-and-ritual',
    name: 'Rhythm and Ritual',
    face: 'team',
    difficulty: 'intermediate',
    description: 'Establishing sustainable cadences for prioritization work',
    relatedFramework: 'pf.framework.diagnostic.capabilities.12'
  },
  
  // Organization Face
  {
    id: 'tradeoff-intelligence',
    name: 'Tradeoff Intelligence',
    face: 'organization',
    difficulty: 'advanced',
    description: 'Making explicit, defensible tradeoffs across competing priorities',
    relatedFramework: 'pf.framework.diagnostic.capabilities.12'
  },
  {
    id: 'cost-of-delay-thinking',
    name: 'Cost of Delay Thinking',
    face: 'organization',
    difficulty: 'expert',
    description: 'Quantifying and communicating the economic impact of timing',
    relatedFramework: 'pf.framework.diagnostic.capabilities.12'
  },
  {
    id: 'process-discipline',
    name: 'Process Discipline',
    face: 'organization',
    difficulty: 'intermediate',
    description: 'Consistent execution of prioritization processes',
    relatedFramework: 'pf.framework.diagnostic.capabilities.12'
  },
  
  // Ecosystem Face
  {
    id: 'situational-awareness',
    name: 'Situational Awareness',
    face: 'ecosystem',
    difficulty: 'advanced',
    description: 'Reading context and adapting approach accordingly (STARS)',
    relatedFramework: 'pf.framework.diagnostic.capabilities.12'
  },
  {
    id: 'outcome-reflection',
    name: 'Outcome Reflection',
    face: 'ecosystem',
    difficulty: 'expert',
    description: 'Learning from results to improve future prioritization',
    relatedFramework: 'pf.framework.diagnostic.capabilities.12'
  },
  {
    id: 'avoidance-and-activation',
    name: 'Avoidance & Activation',
    face: 'ecosystem',
    difficulty: 'advanced',
    description: 'Knowing when to avoid premature commitment and when to activate',
    relatedFramework: 'pf.framework.diagnostic.capabilities.12'
  }
];

const weatherConditions: Record<WeatherCondition, { icon: any; label: string; description: string; color: string }> = {
  stable: {
    icon: Sun,
    label: 'Clear Skies (Stable)',
    description: 'Predictable environment - standard routes work well',
    color: 'amber'
  },
  turbulent: {
    icon: CloudRain,
    label: 'Storm Clouds (Turbulent)',
    description: 'Chaos and uncertainty - requires adaptive navigation',
    color: 'charcoal'
  },
  accelerating: {
    icon: Wind,
    label: 'Strong Winds (Accelerating)',
    description: 'Rapid growth - maintain speed while managing risk',
    color: 'sky'
  },
  realigning: {
    icon: Cloud,
    label: 'Shifting Clouds (Realigning)',
    description: 'Strategic shifts - navigate changing terrain',
    color: 'primary'
  },
  startup: {
    icon: CloudSnow,
    label: 'Fresh Snow (Startup)',
    description: 'Uncharted territory - blaze new trails',
    color: 'mint'
  }
};

export const MountainAscent: React.FC = () => {
  const [selectedFace, setSelectedFace] = useState<RoleFace>('individual');
  const [selectedWeather, setSelectedWeather] = useState<WeatherCondition>('stable');
  const [currentElevation, setCurrentElevation] = useState<number>(1000);
  const [expandedRoute, setExpandedRoute] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'overview' | 'routes' | 'elevation'>('overview');

  const facesData: Record<RoleFace, { icon: any; label: string; description: string; color: string }> = {
    individual: {
      icon: Users,
      label: 'Individual Face',
      description: 'Personal capability development - climbing solo',
      color: 'primary'
    },
    team: {
      icon: Users,
      label: 'Team Face',
      description: 'Collective capability - climbing as a rope team',
      color: 'sage'
    },
    organization: {
      icon: Building2,
      label: 'Organization Face',
      description: 'Systemic capability - coordinating multiple teams',
      color: 'coral'
    },
    ecosystem: {
      icon: Globe2,
      label: 'Ecosystem Face',
      description: 'Adaptive capability - navigating complex environments',
      color: 'amber'
    }
  };

  const routesForFace = climbingRoutes.filter(r => r.face === selectedFace);
  const currentCamp = [...elevationCamps].reverse().find(camp => camp.elevation <= currentElevation) || elevationCamps[0];
  const nextCamp = elevationCamps.find(camp => camp.elevation > currentElevation);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-50 via-sage-50 to-amber-50 p-8 border-2 border-consulting-slate-blue-200">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <Mountain className="h-10 w-10 text-consulting-slate-blue-600" />
              <h1 className="text-4xl font-bold text-charcoal-900">The Mountain Ascent</h1>
            </div>
            <p className="text-lg text-charcoal-700 max-w-3xl mb-4">
              Your journey from awareness to mastery. Choose your route, check the conditions, and ascend through five 
              elevation camps representing the DEGAP stages of capability development.
            </p>
            <div className="flex items-center gap-2 text-sm">
              <Info className="h-4 w-4 text-consulting-slate-blue-600" />
              <p className="text-consulting-slate-blue-800">
                <strong>Mountain Metaphor:</strong> Vertical progression through capability levels, with different faces 
                for different role scopes and weather conditions reflecting organizational context (STARS).
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-charcoal-600 mb-1">Current Elevation</div>
            <div className="text-4xl font-bold text-consulting-slate-blue-700">{currentElevation}m</div>
            <div className="text-sm text-sage-700 font-semibold">{currentCamp.name}</div>
          </div>
        </div>
      </div>

      {/* View Mode Selector */}
      <div className="flex gap-2 bg-charcoal-100 p-1 w-fit">
        <button
          onClick={() => setViewMode('overview')}
          className={`
            flex items-center gap-2 px-4 py-2  text-sm font-medium transition-all
            ${viewMode === 'overview' ? 'bg-[var(--bg-raised)] text-consulting-slate-blue-700 shadow-sm' : 'text-charcoal-600 hover:text-charcoal-900'}
          `}
        >
          <Eye className="h-4 w-4" />
          Overview
        </button>
        <button
          onClick={() => setViewMode('routes')}
          className={`
            flex items-center gap-2 px-4 py-2  text-sm font-medium transition-all
            ${viewMode === 'routes' ? 'bg-[var(--bg-raised)] text-sage-700 shadow-sm' : 'text-charcoal-600 hover:text-charcoal-900'}
          `}
        >
          <TrendingUp className="h-4 w-4" />
          Routes
        </button>
        <button
          onClick={() => setViewMode('elevation')}
          className={`
            flex items-center gap-2 px-4 py-2  text-sm font-medium transition-all
            ${viewMode === 'elevation' ? 'bg-[var(--bg-raised)] text-[var(--mustard)] shadow-sm' : 'text-charcoal-600 hover:text-charcoal-900'}
          `}
        >
          <ArrowUp className="h-4 w-4" />
          Elevation
        </button>
      </div>

      {/* Overview Mode */}
      {viewMode === 'overview' && (
        <div className="space-y-6">
          {/* Mountain Visualization */}
          <div className="bg-gradient-to-b from-consulting-slate-blue-100 via-sage-100 to-amber-50 p-8 border-2 border-consulting-slate-blue-300">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Peak Priorities Mountain</h2>
            
            {/* SVG Mountain */}
            <svg viewBox="0 0 800 500" className="w-full h-auto">
              {/* Mountain slopes */}
              <path
                d="M 50 450 L 400 50 L 750 450 Z"
                fill="url(#mountainGradient)"
                stroke="#334e68"
                strokeWidth="3"
              />
              
              {/* Gradient definitions */}
              <defs>
                <linearGradient id="mountainGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#8E4585" stopOpacity="0.3" />
                  <stop offset="25%" stopColor="#8cb299" stopOpacity="0.4" />
                  <stop offset="50%" stopColor="#627d98" stopOpacity="0.5" />
                  <stop offset="75%" stopColor="#f59187" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#f5b969" stopOpacity="0.3" />
                </linearGradient>
              </defs>
              
              {/* Elevation camps */}
              {elevationCamps.map((camp) => {
                const yPos = 450 - (camp.elevation / 7000) * 400;
                const xPos = 400;
                const isCurrent = camp.elevation === currentCamp.elevation;
                
                return (
                  <g key={camp.elevation}>
                    {/* Camp marker */}
                    <circle
                      cx={xPos}
                      cy={yPos}
                      r={isCurrent ? "12" : "8"}
                      fill={isCurrent ? "#8cb299" : "#627d98"}
                      stroke="white"
                      strokeWidth="2"
                      className="cursor-pointer hover:r-10 transition-all"
                      onClick={() => setCurrentElevation(camp.elevation)}
                    />
                    {/* Camp label */}
                    <text
                      x={xPos + 20}
                      y={yPos + 5}
                      fill="#1a2332"
                      fontSize="14"
                      fontWeight={isCurrent ? "bold" : "normal"}
                      className="cursor-pointer"
                      onClick={() => setCurrentElevation(camp.elevation)}
                    >
                      {camp.elevation}m - {camp.degapStage}
                    </text>
                  </g>
                );
              })}
              
              {/* Climbing routes (paths on mountain faces) */}
              <path d="M 200 450 L 400 100" stroke="#627d98" strokeWidth="2" strokeDasharray="5,5" opacity="0.5" />
              <path d="M 300 450 L 400 100" stroke="#8cb299" strokeWidth="2" strokeDasharray="5,5" opacity="0.5" />
              <path d="M 500 450 L 400 100" stroke="#f59187" strokeWidth="2" strokeDasharray="5,5" opacity="0.5" />
              <path d="M 600 450 L 400 100" stroke="#f5b969" strokeWidth="2" strokeDasharray="5,5" opacity="0.5" />
              
              {/* Summit flag */}
              <Flag
                x="385"
                y="35"
                width="30"
                height="30"
                fill="#8E4585"
              />
            </svg>
          </div>

          {/* Mountain Faces Selector */}
          <div>
            <h3 className="text-xl font-bold text-charcoal-900 mb-4">Choose Your Face (Role Scope)</h3>
            <div className="grid md:grid-cols-4 gap-4">
              {(Object.keys(facesData) as RoleFace[]).map(face => {
                const faceData = facesData[face];
                const FaceIcon = faceData.icon;
                const isSelected = selectedFace === face;
                
                return (
                  <button
                    key={face}
                    onClick={() => setSelectedFace(face)}
                    className={`
                      p-4  border-2 transition-all text-left
                      ${isSelected 
                        ? `bg-${faceData.color}-50 border-${faceData.color}-400 shadow-lg` 
                        : 'bg-[var(--bg-raised)] border-[var(--rule)] hover:border-[var(--rule)] hover:shadow'}
                    `}
                  >
                    <FaceIcon className={`h-8 w-8 mb-2 ${isSelected ? `text-${faceData.color}-600` : 'text-charcoal-500'}`} />
                    <h4 className={`font-bold mb-1 ${isSelected ? `text-${faceData.color}-900` : 'text-charcoal-800'}`}>
                      {faceData.label}
                    </h4>
                    <p className="text-sm text-charcoal-600">{faceData.description}</p>
                    <div className="mt-2 text-xs text-charcoal-500">
                      {routesForFace.length} routes available
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Weather Conditions */}
          <div>
            <h3 className="text-xl font-bold text-charcoal-900 mb-4">Check Conditions (STARS Context)</h3>
            <div className="grid md:grid-cols-5 gap-3">
              {(Object.keys(weatherConditions) as WeatherCondition[]).map(weather => {
                const condition = weatherConditions[weather];
                const WeatherIcon = condition.icon;
                const isSelected = selectedWeather === weather;
                
                return (
                  <button
                    key={weather}
                    onClick={() => setSelectedWeather(weather)}
                    className={`
                      p-3  border-2 transition-all
                      ${isSelected 
                        ? 'bg-consulting-slate-blue-50 border-consulting-slate-blue-400 shadow-md' 
                        : 'bg-[var(--bg-raised)] border-[var(--rule)] hover:border-[var(--rule)]'}
                    `}
                  >
                    <WeatherIcon className={`h-6 w-6 mb-2 mx-auto ${isSelected ? 'text-consulting-slate-blue-600' : 'text-charcoal-500'}`} />
                    <div className={`text-sm font-semibold text-center ${isSelected ? 'text-consulting-slate-blue-900' : 'text-charcoal-700'}`}>
                      {condition.label}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="mt-3 p-4 bg-[var(--bg-raised)] border border-[var(--rule)]">
              <p className="text-sm text-charcoal-700">
                <strong>Current conditions:</strong> {weatherConditions[selectedWeather].description}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Routes Mode */}
      {viewMode === 'routes' && (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-charcoal-900">
            Climbing Routes on the {facesData[selectedFace].label}
          </h2>
          <p className="text-charcoal-700 mb-6">
            {facesData[selectedFace].description} - {routesForFace.length} routes available
          </p>
          
          {routesForFace.map(route => {
            const isExpanded = expandedRoute === route.id;
            const difficultyColors = {
              beginner: 'green',
              intermediate: 'amber',
              advanced: 'coral',
              expert: 'plum'
            };
            const color = difficultyColors[route.difficulty];
            
            return (
              <div key={route.id} className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] overflow-hidden">
                <button
                  onClick={() => setExpandedRoute(isExpanded ? null : route.id)}
                  className="w-full p-6 text-left hover:bg-[var(--surface)] transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <TrendingUp className="h-5 w-5 text-sage-600" />
                        <h3 className="text-lg font-bold text-charcoal-900">{route.name}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium bg-${color}-100 text-${color}-800`}>
                          {route.difficulty}
                        </span>
                      </div>
                      <p className="text-charcoal-700">{route.description}</p>
                    </div>
                    <Sparkles className={`h-5 w-5 ${isExpanded ? 'text-sage-600' : 'text-[var(--text-ter)]'}`} />
                  </div>
                </button>
                
                {isExpanded && (
                  <div className="px-6 pb-6 border-t border-[var(--rule)] pt-4 bg-sage-50">
                    <h4 className="font-semibold text-charcoal-900 mb-3">Progression along this route:</h4>
                    <div className="space-y-3">
                      {elevationCamps.map(camp => (
                        <div key={camp.elevation} className="flex items-start gap-3">
                          <div className={`
                            flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center
                            ${camp.elevation <= currentElevation 
                              ? 'bg-sage-600 text-white' 
                              : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'}
                          `}>
                            {camp.elevation <= currentElevation ? (
                              <CheckCircle2 className="h-5 w-5" />
                            ) : (
                              <Circle className="h-5 w-5" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-charcoal-900">{camp.name}</div>
                            <div className="text-sm text-charcoal-600">{camp.description}</div>
                            <div className="text-xs text-sage-700 mt-1">
                              Capabilities: {camp.capabilities.join(', ')}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {route.relatedFramework && (
                      <Link
                        to={`/elevation/entry/${route.relatedFramework}`}
                        className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-sage-600 text-white hover:bg-sage-700 transition-colors"
                      >
                        <MapPin className="h-4 w-4" />
                        View Full Framework
                      </Link>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Elevation Mode */}
      {viewMode === 'elevation' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Your Elevation Progress</h2>
            <div className="bg-gradient-to-r from-amber-50 to-plum-100 p-6 border-2 border-[var(--mustard)]">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-sm text-charcoal-600">Current Camp</div>
                  <div className="text-3xl font-bold text-charcoal-900">{currentCamp.name}</div>
                </div>
                {nextCamp && (
                  <div className="text-right">
                    <div className="text-sm text-charcoal-600">Next Camp</div>
                    <div className="text-2xl font-bold text-sage-700">{nextCamp.name}</div>
                    <div className="text-sm text-charcoal-600">{nextCamp.elevation - currentElevation}m to go</div>
                  </div>
                )}
              </div>
              
              {/* Progress bar */}
              <div className="relative h-4 bg-[var(--bg-raised)] rounded-full overflow-hidden">
                <div 
                  className="absolute h-full bg-gradient-to-r from-amber-500 to-sage-600 transition-all duration-500"
                  style={{ width: `${(currentElevation / 7000) * 100}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-charcoal-600">
                <span>Base: 1000m</span>
                <span>Summit: 7000m</span>
              </div>
            </div>
          </div>

          {/* All Camps Detail */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-charcoal-900">All Elevation Camps (DEGAP Stages)</h3>
            {elevationCamps.map((camp, idx) => {
              const isPassed = camp.elevation < currentElevation;
              const isCurrent = camp.elevation === currentElevation;

              return (
                <div 
                  key={camp.elevation}
                  className={`
                     border-2 overflow-hidden transition-all
                    ${isCurrent 
                      ? `border-${camp.color}-400 shadow-lg` 
                      : isPassed 
                        ? 'border-sage-200 opacity-75' 
                        : 'border-[var(--rule)]'}
                  `}
                >
                  <div className={`
                    px-6 py-4 bg-gradient-to-r 
                    ${isCurrent 
                      ? `from-${camp.color}-50 to-${camp.color}-100` 
                      : isPassed 
                        ? 'from-sage-50 to-sage-100' 
                        : 'from-gray-50 to-gray-100'}
                  `}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`
                          w-12 h-12 rounded-full flex items-center justify-center
                          ${isCurrent 
                            ? `bg-${camp.color}-600 text-white` 
                            : isPassed 
                              ? 'bg-sage-600 text-white' 
                              : 'bg-[var(--rule-bright)] text-[var(--text-sec)]'}
                        `}>
                          {isPassed ? (
                            <CheckCircle2 className="h-6 w-6" />
                          ) : (
                            <span className="font-bold">{idx + 1}</span>
                          )}
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-charcoal-900">{camp.name}</div>
                          <div className="text-sm text-charcoal-600">{camp.elevation}m elevation</div>
                        </div>
                      </div>
                      {isCurrent && (
                        <span className="px-3 py-1 bg-sage-600 text-white rounded-full text-sm font-medium">
                          You are here
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="px-6 py-4 bg-[var(--bg-raised)]">
                    <p className="text-charcoal-700 mb-3">{camp.description}</p>
                    <div>
                      <h4 className="text-sm font-semibold text-charcoal-900 mb-2">Key Capabilities at this level:</h4>
                      <div className="flex flex-wrap gap-2">
                        {camp.capabilities.map(cap => (
                          <span key={cap} className={`px-3 py-1 rounded-full text-sm bg-${camp.color}-100 text-${camp.color}-800`}>
                            {cap}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Key Insights */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-100 to-sage-100 p-8 border-2 border-consulting-slate-blue-300">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Mountain Metaphor: Key Insights</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-bold text-consulting-slate-blue-900 mb-2 flex items-center gap-2">
              <Mountain className="h-5 w-5" />
              Vertical Progression
            </h3>
            <p className="text-charcoal-700 text-sm">
              Unlike the Island's horizontal exploration of domains, the Mountain represents vertical capability development - 
              ascending from awareness through mastery along the five DEGAP stages.
            </p>
          </div>
          <div>
            <h3 className="font-bold text-sage-900 mb-2 flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Multiple Routes
            </h3>
            <p className="text-charcoal-700 text-sm">
              The 12 Capability Dimensions are different climbing routes, each with its own difficulty and character. 
              You can ascend via multiple routes simultaneously or focus on one path.
            </p>
          </div>
          <div>
            <h3 className="font-bold text-coral-900 mb-2 flex items-center gap-2">
              <Users className="h-5 w-5" />
              Four Faces
            </h3>
            <p className="text-charcoal-700 text-sm">
              Each mountain face (Individual, Team, Organization, Ecosystem) represents a different role scope, 
              with routes suited to that context.
            </p>
          </div>
          <div>
            <h3 className="font-bold text-[var(--mustard)] mb-2 flex items-center gap-2">
              <Cloud className="h-5 w-5" />
              Weather = Context
            </h3>
            <p className="text-charcoal-700 text-sm">
              STARS conditions are represented as weather patterns. Different conditions require different gear, 
              pacing, and route selection.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

